import React, { useCallback, useEffect, useState } from 'react';
import {
  createJobOpening,
  deleteJobOpening,
  listJobOpenings,
  updateJobOpening,
} from '../api/jobOpenings';
import { useSearchParams } from 'react-router-dom';
import Spinner from '../components/ui/Spinner';
import { useToast } from '../components/ui/Toast';
import { listMyOrgs } from '../api/orgs';
import { INDUSTRY_SUGGESTIONS, SENIORITY_OPTIONS, listPinnableVersions, titleCase } from '../api/blueprints';

const EMPTY_FORM = {
  title: '',
  department: '',
  location: '',
  employmentType: 'full-time',
  description: '',
  industry: '',
  seniority: '',
  blueprintVersionId: '',
};

export default function JobOpeningsPage() {
  const toast = useToast();
  const [openings, setOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [versions, setVersions] = useState([]);
  const [searchParams] = useSearchParams();

  // Blueprint Phase 12C: published versions this org can pin (Samastix Library + own blueprints).
  useEffect(() => {
    listMyOrgs()
      .then((res) => listPinnableVersions((Array.isArray(res.data) ? res.data : [])[0]?.id))
      .then(setVersions)
      .catch(() => setVersions([]));
  }, []);

  // "Use" from the blueprint library lands here with the version preselected.
  useEffect(() => {
    const v = searchParams.get('blueprintVersionId');
    if (v) {
      setForm((f) => ({ ...f, blueprintVersionId: v }));
      setShowForm(true);
    }
  }, [searchParams]);

  const load = useCallback(async () => {
    try {
      const res = await listJobOpenings();
      setOpenings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load job openings');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const onCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await createJobOpening({
        ...form,
        industry: form.industry || null,
        seniority: form.seniority || null,
        blueprintVersionId: form.blueprintVersionId || null,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      toast.success('Job opening published');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create opening');
    } finally {
      setCreating(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await updateJobOpening(id, { status });
      toast.success(`Opening marked ${status.toLowerCase()}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  // Blueprint Phase 16: switching a job to a newer version is always an explicit action.
  const switchVersion = async (job) => {
    const p = job.pinnedBlueprint;
    if (!window.confirm(`Move "${job.title}" from ${p.name} v${p.versionNo} to v${p.latestVersionNo}? `
      + 'Candidates already invited keep their current assessment; new candidates get the new version.')) return;
    try {
      await updateJobOpening(job.id, { blueprintVersionId: p.latestVersionId });
      toast.success(`Now using v${p.latestVersionNo}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const changeBlueprint = async (job, versionId) => {
    try {
      await updateJobOpening(job.id, versionId ? { blueprintVersionId: versionId } : { clearBlueprint: true });
      toast.success(versionId ? 'Interview blueprint pinned' : 'Blueprint removed; the default resolution applies');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Remove this job opening?')) return;
    try {
      await deleteJobOpening(id);
      toast.success('Opening removed');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" label="Loading job openings..." />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink">Job Openings</h1>
          <p className="text-sm text-ink-muted mt-1">Publish roles candidates can browse in the marketplace.</p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="btn btn-primary"
        >
          {showForm ? 'Cancel' : 'New opening'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={onCreate} className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <input
            required
            placeholder="Job title"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
          <div className="grid sm:grid-cols-3 gap-3">
            <input
              placeholder="Department"
              value={form.department}
              onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
            <input
              placeholder="Location"
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
            <select
              value={form.employmentType}
              onChange={(e) => setForm((f) => ({ ...f, employmentType: e.target.value }))}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="contract">Contract</option>
              <option value="internship">Internship</option>
            </select>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <input
              placeholder="Industry (e.g. fintech)"
              list="job-industries"
              value={form.industry}
              onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
            <datalist id="job-industries">{INDUSTRY_SUGGESTIONS.map((i) => <option key={i} value={i} />)}</datalist>
            <select
              value={form.seniority}
              onChange={(e) => setForm((f) => ({ ...f, seniority: e.target.value }))}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Seniority</option>
              {SENIORITY_OPTIONS.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
            </select>
            <select
              value={form.blueprintVersionId}
              onChange={(e) => setForm((f) => ({ ...f, blueprintVersionId: e.target.value }))}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
              title="The interview blueprint version every candidate for this job is assessed with"
            >
              <option value="">Interview blueprint (default)</option>
              {versions.map((v) => <option key={v.versionId} value={v.versionId}>{v.label}</option>)}
            </select>
          </div>
          <textarea
            required
            rows={6}
            placeholder="Job description (used for ATS matching)"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={creating}
            className="btn btn-primary disabled:opacity-50"
          >
            {creating ? 'Publishing...' : 'Publish opening'}
          </button>
        </form>
      )}

      {!openings.length ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center text-slate-500">
          No job openings yet. Create one to appear in the candidate marketplace.
        </div>
      ) : (
        <div className="space-y-3">
          {openings.map((job) => (
            <div key={job.id} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">{job.title}</div>
                <div className="text-sm text-slate-500">
                  {[job.location, job.department, job.employmentType].filter(Boolean).join(' · ')}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {[job.industry && titleCase(job.industry), job.seniority && titleCase(job.seniority)].filter(Boolean).join(' · ')}
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                  <span className="text-slate-500">Blueprint:</span>
                  {job.pinnedBlueprint ? (
                    <span className="font-medium text-slate-800">{job.pinnedBlueprint.name} · v{job.pinnedBlueprint.versionNo}</span>
                  ) : (
                    <span className="text-slate-500">Default (org default if compatible, else Samastix general)</span>
                  )}
                  {job.pinnedBlueprint?.updateAvailable && (
                    <button type="button" onClick={() => switchVersion(job)} className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                      Update available: v{job.pinnedBlueprint.latestVersionNo}
                    </button>
                  )}
                  <select
                    aria-label="Change interview blueprint"
                    value=""
                    onChange={(e) => e.target.value && changeBlueprint(job, e.target.value === 'none' ? null : e.target.value)}
                    className="border border-slate-200 rounded-lg px-2 py-0.5"
                  >
                    <option value="">Change…</option>
                    {versions.map((v) => <option key={v.versionId} value={v.versionId}>{v.label}</option>)}
                    {job.pinnedBlueprint && <option value="none">Remove pin</option>}
                  </select>
                </div>
                <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full ${
                  job.status === 'OPEN' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                }`}
                >
                  {job.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 items-start">
                {job.status === 'OPEN' && (
                  <button type="button" onClick={() => setStatus(job.id, 'CLOSED')} className="text-sm px-3 py-1 border rounded-lg">
                    Close
                  </button>
                )}
                {job.status === 'CLOSED' && (
                  <button type="button" onClick={() => setStatus(job.id, 'OPEN')} className="text-sm px-3 py-1 border rounded-lg">
                    Reopen
                  </button>
                )}
                <button type="button" onClick={() => onDelete(job.id)} className="text-sm px-3 py-1 text-red-600 border border-red-200 rounded-lg">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
